/**
 * HorseNotesSection — Notes card + Add/Edit Note dialog + ViewAll + FilePreview.
 *
 * Props:
 *  horse               — the horse object
 *  canEditAccount      — boolean
 *  conformationNotes   — array from parent useQuery
 *  notesLoading        — boolean
 *  invalidateNotes     — callback
 *  invalidateTasks     — callback (for create_follow_up_task)
 *  onRegisterNoteOpener — fn => void  — registers handleNoteClick so parent can pass it to TaskSection
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Skeleton } from '../../ui/skeleton';
import { Collapsible, CollapsibleContent } from '../../ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  FileText, Upload, Camera, X, Trash2, RotateCw, File,
  Eye, Globe, Undo2, User, Video, Image, Loader2, Download,
  CheckCircle2, Calendar, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { activityAPI } from '../../../services/api';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useConfirm } from '../../common/ConfirmDialog';
import { SectionHeader } from '../../common/OverviewHelpers';

// ── Constants ─────────────────────────────────────────────────────────────────
const NOTE_TYPE_OPTIONS = [
  { value: 'conformation_note',     label: 'Conformation Note' },
  { value: 'vet_procedure_note',    label: 'Vet Procedure Note' },
  { value: 'layup_note',           label: 'Layup Note' },
  { value: 'sales_inspection_note', label: 'Sales Inspection Note' },
  { value: 'other_note',           label: 'Other Note' },
];
const NOTE_TYPE_BADGE = {
  conformation_note:     'Conformation',
  vet_procedure_note:    'Vet Procedure',
  layup_note:           'Layup',
  sales_inspection_note: 'Sale Inspection',
  other_note:           'Other',
};
const NOTE_TYPE_COLOR = {
  conformation_note:     'bg-blue-100 text-blue-700',
  vet_procedure_note:    'bg-green-100 text-green-700',
  layup_note:           'bg-yellow-100 text-yellow-700',
  sales_inspection_note: 'bg-purple-100 text-purple-700',
  other_note:           'bg-gray-100 text-gray-600',
};

const getNoteFileIcon = (filename, cls = 'w-4 h-4 text-muted-foreground') => {
  if (!filename) return <File className={cls} />;
  const ext = filename.split('.').pop().toLowerCase();
  if (['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v'].includes(ext)) return <Video className={cls} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'bmp'].includes(ext)) return <Image className={cls} />;
  if (ext === 'pdf') return <FileText className={cls} />;
  return <File className={cls} />;
};

const SectionSkeleton = ({ rows = 3 }) => (
  <div className="space-y-2 mt-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 p-2">
        <Skeleton className="h-4 w-4 rounded-full shrink-0" />
        <Skeleton className="h-4 flex-1 rounded" />
        <Skeleton className="h-4 w-16 rounded" />
      </div>
    ))}
  </div>
);

const BLANK_NOTE_FORM = {
  note_type: 'conformation_note',
  date: new Date().toISOString().split('T')[0],
  by: '',
  title: '',
  notes: '',
  grade: '',
  picture_url: '',
  picture_file_id: '',
  file_url: '',
  file_name: '',
  file_id: '',
  publish_to_portal: false,
  create_follow_up_task: false,
};

const HorseNotesSection = ({
  horse,
  canEditAccount,
  conformationNotes = [],
  notesLoading,
  invalidateNotes,
  invalidateTasks,
  onRegisterNoteOpener,
}) => {
  const confirm = useConfirm();

  // ── Section state ──────────────────────────────────────────────────────────
  const [sectionOpen, setSectionOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [allNotesDialogOpen, setAllNotesDialogOpen] = useState(false);
  const [notesSearchTerm, setNotesSearchTerm] = useState('');

  // ── Note dialog state ──────────────────────────────────────────────────────
  const [addNoteOpen,     setAddNoteOpen]     = useState(false);
  const [noteDetailOpen,  setNoteDetailOpen]  = useState(false);
  const [selectedNote,    setSelectedNote]    = useState(null);
  const [isEditingNote,   setIsEditingNote]   = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [noteForm,        setNoteForm]        = useState(BLANK_NOTE_FORM);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [picturePreview,  setPicturePreview]  = useState(null);
  const [pictureBlob,     setPictureBlob]     = useState(null);
  const [pendingSaveNote, setPendingSaveNote] = useState(false);
  const [linkedTaskData,  setLinkedTaskData]  = useState(null);

  // ── Note file preview state ─────────────────────────────────────────────────
  const [noteFilePreviewOpen,    setNoteFilePreviewOpen]    = useState(false);
  const [noteFilePreviewUrl,     setNoteFilePreviewUrl]     = useState(null);
  const [noteFilePreviewLoading, setNoteFilePreviewLoading] = useState(false);
  const [noteFilePreviewMime,    setNoteFilePreviewMime]    = useState('');

  // ── File upload hook ────────────────────────────────────────────────────────
  const {
    uploadFile:          uploadNoteFile,
    isCompressing:       noteFileCompressing,
    compressionProgress: noteFileCompressionProgress,
    isUploading:         uploadingNoteFile,
  } = useFileUpload({
    category:  'note_file',
    horseId:   horse?.id,
    onSuccess: (data) => {
      setNoteForm(prev => ({
        ...prev,
        file_url:  `/api/files/${data.id}`,
        file_name: data.filename || data.original_filename || data.id,
        file_id:   data.id,
      }));
      toast.success('File uploaded');
    },
  });

  // Register the handleNoteClick function with the parent so TaskSection can use it
  useEffect(() => {
    onRegisterNoteOpener?.(handleNoteClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAddNote = () => {
    setNoteForm({ ...BLANK_NOTE_FORM, date: new Date().toISOString().split('T')[0] });
    setPicturePreview(null);
    setPictureBlob(null);
    setAddNoteOpen(true);
  };

  const handleNoteClick = async (note) => {
    setSelectedNote(note);
    setNoteForm({
      note_type: note.note_type || 'conformation_note',
      date: note.date ? note.date.split('T')[0] : new Date().toISOString().split('T')[0],
      by: note.by || '',
      title: note.title || '',
      notes: note.notes || '',
      grade: note.grade || '',
      picture_url: note.picture_url || '',
      picture_file_id: note.picture_file_id || '',
      file_url: note.file_url || '',
      file_name: note.file_name || '',
      file_id: note.file_id || '',
      publish_to_portal: note.published_to_portal || false,
      create_follow_up_task: !!note.follow_up_task_id,
    });
    // Load picture
    if (note.picture_url) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(note.picture_url, { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setPicturePreview(url); setPictureBlob(url);
        } else { setPicturePreview(null); setPictureBlob(null); }
      } catch { setPicturePreview(null); setPictureBlob(null); }
    } else { setPicturePreview(null); setPictureBlob(null); }
    // Fetch linked task
    setLinkedTaskData(null);
    if (note.follow_up_task_id) {
      try {
        const taskRes = await activityAPI.list({ horse_id: note.horse_id || horse.id });
        setLinkedTaskData((taskRes.data || []).find(t => t.id === note.follow_up_task_id) || null);
      } catch { setLinkedTaskData(null); }
    }
    setNoteDetailOpen(true);
  };

  const handleSaveNote = async (confirmed = false) => {
    if (!noteForm.title.trim()) { toast.error('Title is required'); return; }
    if (noteForm.publish_to_portal && !confirmed) {
      const wasPublished = isEditingNote ? selectedNote?.published_to_portal : false;
      if (!wasPublished) { setPendingSaveNote(true); return; }
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...noteForm, horse_id: horse.id };
      if (!addNoteOpen && selectedNote?.follow_up_task_id) payload.create_follow_up_task = false;
      if (payload.note_type !== 'conformation_note') payload.grade = '';
      let savedTaskId = null;

      if (addNoteOpen) {
        const res = await fetch(`${window.location.origin}/api/conformation-notes`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to create note');
        const created = await res.json();
        savedTaskId = created.follow_up_task_id || null;
      } else {
        const res = await fetch(`${window.location.origin}/api/conformation-notes/${selectedNote.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to update note');
        const updated = await res.json();
        savedTaskId = updated.follow_up_task_id || null;
      }

      toast.success('Note saved');
      const wantedTask = payload.create_follow_up_task && savedTaskId;
      if (addNoteOpen) {
        setAddNoteOpen(false); setNoteDetailOpen(false);
        setPicturePreview(null); setPictureBlob(null);
        invalidateNotes?.();
        if (wantedTask) {
          invalidateTasks?.();
          toast.info('Follow-up task created — view it in the Tasks section');
        }
      } else {
        setIsEditingNote(false);
        invalidateNotes?.();
        if (selectedNote?.id) {
          const tkn = localStorage.getItem('token');
          const res = await fetch(`${window.location.origin}/api/conformation-notes/${selectedNote.id}`, { headers: { Authorization: `Bearer ${tkn}` } });
          if (res.ok) setSelectedNote(await res.json());
        }
        if (wantedTask) {
          invalidateTasks?.();
          toast.info('Follow-up task created — view it in the Tasks section');
        }
      }
      setIsEditingNote(false);
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save note');
    } finally { setSaving(false); }
  };

  const handleDeleteNote = async () => {
    if (!selectedNote) return;
    if (!await confirm({ title: 'Delete this note?', destructive: true })) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${window.location.origin}/api/conformation-notes/${selectedNote.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Note deleted');
      setNoteDetailOpen(false); setIsEditingNote(false);
      invalidateNotes?.();
    } catch { toast.error('Failed to delete note'); }
    finally { setSaving(false); }
  };

  const handleTogglePublish = async (note) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${window.location.origin}/api/conformation-notes/${note.id}/publish-to-portal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(data.published_to_portal ? 'Note published to portal' : 'Note unpublished');
      invalidateNotes?.();
      if (selectedNote?.id === note.id) setSelectedNote(prev => ({ ...prev, ...data }));
    } catch { toast.error('Failed to update publish status'); }
  };

  const handlePictureUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }
    setUploadingPicture(true);
    try {
      setPicturePreview(URL.createObjectURL(file));
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file); formData.append('category', 'note_photo'); formData.append('horse_id', horse.id);
      const res = await fetch(`${window.location.origin}/api/files/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const fileUrl = `/api/files/public/${data.id}`;
      setNoteForm(prev => ({ ...prev, picture_url: fileUrl, picture_file_id: data.id }));
      setPicturePreview(`${window.location.origin}${fileUrl}`);
      toast.success('Picture uploaded');
    } catch { toast.error('Failed to upload picture'); setPicturePreview(null); }
    finally { setUploadingPicture(false); }
  };

  const handleTakePicture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream; video.play();
      await new Promise(r => { video.onloadedmetadata = r; });
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      stream.getTracks().forEach(t => t.stop());
      canvas.toBlob(async blob => {
        await handlePictureUpload(new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.9);
    } catch { toast.error('Failed to access camera. Please check permissions.'); }
  };

  const handleRemovePicture = async () => {
    const fileId = noteForm.picture_file_id;
    if (fileId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${window.location.origin}/api/files/${fileId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      } catch { /* ignore */ }
    }
    setNoteForm(prev => ({ ...prev, picture_url: '', picture_file_id: '' }));
    setPicturePreview(null); setPictureBlob(null);
  };

  const handleOpenNoteFilePreview = async () => {
    if (!selectedNote?.file_id) return;
    setNoteFilePreviewOpen(true); setNoteFilePreviewLoading(true);
    setNoteFilePreviewMime(''); setNoteFilePreviewUrl(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${window.location.origin}/api/files/${selectedNote.file_id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      setNoteFilePreviewMime(blob.type);
      setNoteFilePreviewUrl(URL.createObjectURL(blob));
    } catch { toast.error('Failed to load file preview'); }
    finally { setNoteFilePreviewLoading(false); }
  };

  const handleCloseNoteFilePreview = () => {
    setNoteFilePreviewOpen(false);
    if (noteFilePreviewUrl) { URL.revokeObjectURL(noteFilePreviewUrl); setNoteFilePreviewUrl(null); }
    setNoteFilePreviewMime('');
  };

  // ── Filtered notes ──────────────────────────────────────────────────────────
  const filteredNotes = conformationNotes.filter(note => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    const typeBadge = (NOTE_TYPE_BADGE[note.note_type || 'conformation_note'] || '').toLowerCase();
    return note.title?.toLowerCase().includes(s) || note.by?.toLowerCase().includes(s) ||
           note.grade?.toLowerCase().includes(s) || note.notes?.toLowerCase().includes(s) || typeBadge.includes(s);
  });
  const displayedNotes = filteredNotes.slice(0, 5);

  return (
    <>
      {/* ── Notes Section Card ────────────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-6">
          <Collapsible open={sectionOpen} onOpenChange={setSectionOpen}>
            <SectionHeader
              title="Notes"
              icon={FileText}
              isOpen={sectionOpen}
              onToggle={() => setSectionOpen(o => !o)}
              action={canEditAccount ? handleAddNote : undefined}
            />
            <CollapsibleContent className="mt-4">
              <div className="mb-3">
                <Input
                  placeholder="Search notes..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              {notesLoading ? <SectionSkeleton rows={3} /> : filteredNotes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {searchTerm ? 'No matching notes found.' : 'No notes yet.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {displayedNotes.map(note => {
                    const nt = note.note_type || 'conformation_note';
                    return (
                      <div key={note.id} className="p-3 border rounded hover:bg-muted/50 cursor-pointer" onClick={() => handleNoteClick(note)} data-testid={`note-${note.id}`}>
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-medium text-sm">{note.title}</p>
                          <div className="flex items-center gap-1.5">
                            {note.published_to_portal && <span className="text-[10px] text-muted-foreground font-medium">Published</span>}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${NOTE_TYPE_COLOR[nt]}`}>{NOTE_TYPE_BADGE[nt] || nt}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs text-muted-foreground">
                          <div className="flex gap-4">
                            {note.date && <span>{new Date(`${note.date.split('T')[0]}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                            {note.by && <span>By: {note.by}</span>}
                          </div>
                          {nt === 'conformation_note' && note.grade && <Badge variant="outline" className="text-xs">{note.grade}</Badge>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="pt-3 border-t mt-3">
                <Button variant="outline" size="sm" className="w-full" onClick={() => setAllNotesDialogOpen(true)}>
                  View All Notes ({conformationNotes.length})
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* ── Add/Edit Note Dialog ──────────────────────────────────────────── */}
      <Dialog open={addNoteOpen || noteDetailOpen} onOpenChange={open => {
        if (!open) { setAddNoteOpen(false); setNoteDetailOpen(false); setIsEditingNote(false); setPicturePreview(null); setPictureBlob(null); setPendingSaveNote(false); }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{addNoteOpen ? 'Add Note' : isEditingNote ? 'Edit Note' : 'Note'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {(addNoteOpen || isEditingNote) ? (
              <>
                {/* Note Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Note Type *</label>
                  <Select value={noteForm.note_type} onValueChange={v => setNoteForm({...noteForm, note_type: v, grade: v !== 'conformation_note' ? '' : noteForm.grade})}>
                    <SelectTrigger><SelectValue placeholder="Select note type" /></SelectTrigger>
                    <SelectContent>{NOTE_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Date</label>
                    <Input type="date" value={noteForm.date} onChange={e => setNoteForm({...noteForm, date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">By</label>
                    <Input value={noteForm.by} onChange={e => setNoteForm({...noteForm, by: e.target.value})} placeholder="Person's name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title *</label>
                  <Input value={noteForm.title} onChange={e => setNoteForm({...noteForm, title: e.target.value})} placeholder="Brief title" />
                </div>
                {noteForm.note_type === 'conformation_note' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Grade</label>
                    <Input value={noteForm.grade} onChange={e => setNoteForm({...noteForm, grade: e.target.value})} placeholder="e.g., A, B+, Good" />
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Notes</label>
                  <Textarea value={noteForm.notes} onChange={e => setNoteForm({...noteForm, notes: e.target.value})} placeholder="Detailed notes..." rows={4} />
                </div>
                {/* Photo */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Photo</label>
                  {(picturePreview || pictureBlob || noteForm.picture_url) ? (
                    <div className="relative">
                      <img
                        src={picturePreview || pictureBlob || (noteForm.picture_url?.startsWith('http') ? noteForm.picture_url : `${window.location.origin}${noteForm.picture_url}`)}
                        alt="Preview" className="w-full h-auto rounded-lg border max-h-48 object-contain"
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button variant="outline" size="icon" className="bg-white/80 hover:bg-white h-7 w-7" title="Rotate 90°" type="button"
                          onClick={async () => {
                            const imgSrc = picturePreview || pictureBlob || (noteForm.picture_url?.startsWith('http') ? noteForm.picture_url : `${window.location.origin}${noteForm.picture_url}`);
                            try {
                              const img = new window.Image(); img.crossOrigin = 'anonymous';
                              await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = imgSrc; });
                              const canvas = document.createElement('canvas');
                              canvas.width = img.height; canvas.height = img.width;
                              const ctx = canvas.getContext('2d');
                              ctx.translate(canvas.width / 2, canvas.height / 2); ctx.rotate(90 * Math.PI / 180);
                              ctx.drawImage(img, -img.width / 2, -img.height / 2);
                              const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.92));
                              setPicturePreview(URL.createObjectURL(blob)); setPictureBlob(null);
                              const token = localStorage.getItem('token');
                              const oldId = noteForm.picture_file_id;
                              if (oldId) await fetch(`${window.location.origin}/api/files/${oldId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                              const fd = new FormData();
                              fd.append('file', blob, 'rotated.jpg'); fd.append('category', 'note_photo'); fd.append('horse_id', horse.id);
                              const resp = await fetch(`${window.location.origin}/api/files/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                              if (resp.ok) { const data = await resp.json(); setNoteForm(prev => ({ ...prev, picture_url: `/api/files/public/${data.id}`, picture_file_id: data.id })); toast.success('Photo rotated'); }
                            } catch { toast.error('Failed to rotate'); }
                          }}><RotateCw className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="destructive" size="icon" className="h-7 w-7" onClick={handleRemovePicture} type="button"><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => document.getElementById('picture-gallery-input').click()} disabled={uploadingPicture}>
                        <Upload className="w-4 h-4 mr-2" />{uploadingPicture ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                      <Button type="button" variant="outline" onClick={handleTakePicture} disabled={uploadingPicture}>
                        <Camera className="w-4 h-4 mr-2" />Take Photo
                      </Button>
                      <input id="picture-gallery-input" type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { handlePictureUpload(f); e.target.value = ''; } }} />
                      <input id="picture-upload-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { handlePictureUpload(f); e.target.value = ''; } }} />
                    </div>
                  )}
                </div>
                {/* File */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">File</label>
                  {noteForm.file_id ? (
                    <div className="flex items-center gap-2 p-2 border rounded bg-muted/30">
                      {getNoteFileIcon(noteForm.file_name, 'w-4 h-4 text-muted-foreground shrink-0')}
                      <span className="text-sm flex-1 truncate">{noteForm.file_name}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setNoteForm(p => ({...p, file_url: '', file_name: '', file_id: ''}))} type="button">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {noteFileCompressing && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Compressing video…</span><span>{noteFileCompressionProgress}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div className="bg-primary h-1.5 rounded-full transition-[width]" style={{ width: `${noteFileCompressionProgress}%` }} />
                          </div>
                        </div>
                      )}
                      <Button type="button" variant="outline" className="w-full" onClick={() => document.getElementById('note-file-input').click()} disabled={uploadingNoteFile || noteFileCompressing}>
                        <Upload className="w-4 h-4 mr-2" />{noteFileCompressing ? 'Compressing…' : uploadingNoteFile ? 'Uploading…' : 'Upload File'}
                      </Button>
                      <input id="note-file-input" type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { uploadNoteFile(f); e.target.value = ''; } }} />
                    </div>
                  )}
                </div>
                {/* Bottom checkboxes */}
                <div className="space-y-2 pt-2 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded accent-primary" checked={noteForm.publish_to_portal}
                      onChange={e => { setNoteForm(p => ({...p, publish_to_portal: e.target.checked})); if (!e.target.checked) setPendingSaveNote(false); }} />
                    <span className="text-sm">Publish to Owner Portal</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded accent-primary"
                      checked={!!(isEditingNote && selectedNote?.follow_up_task_id) || noteForm.create_follow_up_task}
                      onChange={e => setNoteForm(p => ({...p, create_follow_up_task: e.target.checked}))}
                      disabled={!!(isEditingNote && selectedNote?.follow_up_task_id)} />
                    <span className="text-sm">Create Follow-up Task</span>
                  </label>
                </div>
              </>
            ) : (
              /* View mode */
              <div className="space-y-3">
                <div className="p-4 bg-muted/30 rounded">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium text-lg">{selectedNote?.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${NOTE_TYPE_COLOR[selectedNote?.note_type || 'conformation_note']}`}>
                      {NOTE_TYPE_BADGE[selectedNote?.note_type || 'conformation_note']}
                    </span>
                  </div>
                  {selectedNote?.picture_url && (
                    <div className="mb-4">
                      <img src={selectedNote.picture_url?.startsWith('http') ? selectedNote.picture_url : `${window.location.origin}${selectedNote.picture_url}`}
                        alt="Note" className="w-full h-auto rounded-lg border max-h-64 object-contain" />
                    </div>
                  )}
                  {selectedNote?.file_id && (
                    <div className="mb-3 flex items-center gap-2 cursor-pointer hover:bg-muted/40 rounded px-1 py-0.5 -mx-1 group" onClick={handleOpenNoteFilePreview} data-testid="note-file-attachment">
                      {getNoteFileIcon(selectedNote.file_name)}
                      <span className="text-sm text-primary group-hover:underline">{selectedNote.file_name || selectedNote.file_id}</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <span>{selectedNote?.date ? new Date(selectedNote.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</span>
                      </div>
                      {(selectedNote?.note_type === 'conformation_note' || !selectedNote?.note_type) && selectedNote?.grade && (
                        <Badge variant="outline" className="text-xs">{selectedNote.grade}</Badge>
                      )}
                    </div>
                    {selectedNote?.by && (
                      <div className="flex gap-2 items-center">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">By:</span><span>{selectedNote.by}</span>
                      </div>
                    )}
                    {selectedNote?.follow_up_task_id && (
                      <div className="pt-1">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                          <CheckCircle2 className="w-4 h-4" /><span>Linked Task:</span>
                        </div>
                        {linkedTaskData ? (
                          <div className="ml-6 flex items-center gap-2">
                            <span className="text-primary text-sm">{linkedTaskData.activity_name || 'Follow-up Task'}</span>
                            {linkedTaskData.task_status && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">{linkedTaskData.task_status.replace('_', ' ')}</span>
                            )}
                          </div>
                        ) : (
                          <span className="ml-6 text-muted-foreground text-sm">View in the Tasks section</span>
                        )}
                      </div>
                    )}
                    {selectedNote?.notes && (
                      <div className="pt-1">
                        <p className="text-muted-foreground text-sm mb-0.5">Notes:</p>
                        <p className="whitespace-pre-wrap text-sm">{selectedNote.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="pt-2 border-t space-y-1.5">
                  <div className="text-xs text-muted-foreground">
                    {selectedNote?.created_at && (
                      <span>Created {new Date(selectedNote.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{selectedNote.created_by && <> by {selectedNote.created_by}</>}</span>
                    )}
                  </div>
                  {selectedNote?.published_to_portal && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200">
                        <Globe className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                        <span className="text-xs text-blue-700">
                          Published to Portal{selectedNote.published_at ? ` on ${new Date(selectedNote.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                          {selectedNote.published_by ? ` by ${selectedNote.published_by}` : ''}
                        </span>
                      </div>
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600 text-xs h-7" onClick={() => handleTogglePublish(selectedNote)}>
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo Publish
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {(addNoteOpen || isEditingNote) ? (
              <div className="flex justify-between w-full">
                <div>{isEditingNote && <Button variant="ghost" size="icon" onClick={handleDeleteNote} disabled={saving} title="Delete note"><Trash2 className="w-4 h-4" /></Button>}</div>
                {pendingSaveNote ? (
                  <div className="flex flex-col items-center gap-3 w-full border-2 border-foreground rounded-lg p-4">
                    <p className="text-sm font-medium text-center">This will publish the note and all of its contents to the Owner Portal. Please confirm you want to proceed.</p>
                    <div className="flex gap-2 justify-center">
                      <Button variant="outline" size="sm" onClick={() => setPendingSaveNote(false)}>Cancel</Button>
                      <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" onClick={() => { setPendingSaveNote(false); handleSaveNote(true); }}>Confirm &amp; Publish</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setAddNoteOpen(false); setIsEditingNote(false); setPicturePreview(null); setPictureBlob(null); setPendingSaveNote(false); }}>Cancel</Button>
                    <Button onClick={() => handleSaveNote(false)} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-between w-full">
                <Button variant="ghost" size="icon" onClick={() => setIsEditingNote(true)} title="Edit note"><Pencil className="w-4 h-4" /></Button>
                <div />
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── View All Notes Dialog ─────────────────────────────────────────── */}
      <Dialog open={allNotesDialogOpen} onOpenChange={setAllNotesDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>All Notes</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Search notes..." value={notesSearchTerm} onChange={e => setNotesSearchTerm(e.target.value)} className="w-full" />
          </div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {conformationNotes.filter(note => {
              if (!notesSearchTerm) return true;
              const s = notesSearchTerm.toLowerCase();
              return note.title?.toLowerCase().includes(s) || note.by?.toLowerCase().includes(s) ||
                note.notes?.toLowerCase().includes(s) || (NOTE_TYPE_BADGE[note.note_type]||'').toLowerCase().includes(s);
            }).length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No notes found</p>
            ) : conformationNotes.filter(note => {
              if (!notesSearchTerm) return true;
              const s = notesSearchTerm.toLowerCase();
              return note.title?.toLowerCase().includes(s) || note.by?.toLowerCase().includes(s) ||
                note.notes?.toLowerCase().includes(s) || (NOTE_TYPE_BADGE[note.note_type]||'').toLowerCase().includes(s);
            }).map(note => {
              const nt = note.note_type || 'conformation_note';
              return (
                <div key={note.id} className="p-3 border rounded hover:bg-muted/50 cursor-pointer" onClick={() => { handleNoteClick(note); setAllNotesDialogOpen(false); }}>
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-medium text-sm">{note.title}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${NOTE_TYPE_COLOR[nt]}`}>{NOTE_TYPE_BADGE[nt] || nt}</span>
                  </div>
                  {nt === 'conformation_note' && note.grade && <p className="text-xs text-muted-foreground mb-0.5">Grade: {note.grade}</p>}
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    {note.date && <span>{new Date(note.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                    {note.by && <span>By: {note.by}</span>}
                  </div>
                  {note.notes && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{note.notes}</p>}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Note File Preview Dialog ──────────────────────────────────────── */}
      <Dialog open={noteFilePreviewOpen} onOpenChange={open => { if (!open) handleCloseNoteFilePreview(); }}>
        <DialogContent className="max-w-3xl" data-testid="note-file-preview-dialog">
          <DialogHeader><DialogTitle className="truncate">{selectedNote?.file_name || 'File Preview'}</DialogTitle></DialogHeader>
          <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[200px]">
            {noteFilePreviewLoading ? (
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            ) : noteFilePreviewUrl ? (
              noteFilePreviewMime.startsWith('video/')  ? <video src={noteFilePreviewUrl} controls className="max-h-[60vh] max-w-full" /> :
              noteFilePreviewMime.startsWith('image/')  ? <img src={noteFilePreviewUrl} alt="Preview" className="max-h-[60vh] object-contain" /> :
              noteFilePreviewMime === 'application/pdf' ? (
                <object data={noteFilePreviewUrl} type="application/pdf" className="w-full h-[60vh]">
                  <p className="text-sm text-muted-foreground p-4">PDF preview unavailable. Use Download below.</p>
                </object>
              ) : (
                <div className="flex flex-col items-center gap-3 text-muted-foreground"><FileText className="w-16 h-16" /><p className="text-sm">Preview not available for this file type</p></div>
              )
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground"><FileText className="w-16 h-16" /><p className="text-sm">Could not load file</p></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={!noteFilePreviewUrl} onClick={() => {
              if (noteFilePreviewUrl) {
                const a = document.createElement('a'); a.href = noteFilePreviewUrl; a.download = selectedNote?.file_name || 'download';
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
              }
            }}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HorseNotesSection;
